// Reading a tree as "who built on this" rather than "what hangs off what".
//
// The seed page draws the same `SeedTree` two ways: as the lineage graph it
// always was, and as a list of the people who took the song somewhere. This is
// the second reading, and it is the one with the traps in it — the tree carries
// a seed's ANCESTORS as well as its descendants, and a producer's own later
// versions are takes, not remixes of themselves.
//
// Pure, so it can be tested without a DOM (the suite has none).

import type { ApiProducer, SeedTree, TreeNode, RemixOrigin } from "@shared/api-types.js";

/** One row of the remix list. */
export interface RemixItem {
  id: string;
  title: string;
  note: string | null;
  createdAt: string;
  version: number;
  producer: ApiProducer;
  /** Remixes hanging off this one. */
  childCount: number;
  /** Generations below the seed being looked at; 1 is a direct child. */
  depth: number;
  /** Signed audio, when the tree was asked for it and the node has a master. */
  audioUrl?: string;
  /**
   * How many passes this producer made in a row to reach this take. A remixer
   * who came back and improved their remix has remixed the song ONCE — the row
   * leads with the finished take and says how many passes it took.
   */
  takeCount: number;
  /** This node's parent, for walking a producer's own chain back. */
  parentId: string | null;
  /**
   * How far below the focus seed this producer BRANCHED OFF — 1 when they
   * remixed it directly. Not `depth`, which is where their newest take sits:
   * someone who remixed you and then took their own remix again has a tip two
   * down, and calling that "2 generations down" reads as a remix of a remix.
   */
  branchStart: number;
}

export interface RemixReading {
  /** One row per finished remix downstream, newest first, capped by `limit`. */
  items: RemixItem[];
  /** How many there are before `limit` — what a heading counts. */
  total: number;
  /** The producer's OWN later takes downstream, newest first. Not remixes. */
  versions: RemixItem[];
  /** The server cut the tree, or `limit` cut the list. */
  truncated: boolean;
}

const EMPTY: RemixReading = { items: [], total: 0, versions: [], truncated: false };

function itemOf(node: TreeNode, depth: number): RemixItem {
  return {
    id: node.id,
    title: node.title,
    note: node.note,
    createdAt: node.createdAt,
    version: node.version,
    producer: node.producer,
    childCount: node.childCount,
    depth,
    takeCount: 1,
    branchStart: depth,
    parentId: node.parentSeedId,
    ...(node.audioUrl !== undefined ? { audioUrl: node.audioUrl } : {}),
  };
}

/**
 * A remix is its BRANCH TIP: a node whose own producer never took it further.
 * The same rule the API counts by (workers/src/feed.ts songOf), so the list
 * and the number above it can never disagree.
 *
 *   luna → dan1 → dan2     one row  — dan1 is the draft of dan2
 *   luna → danA, danB      two rows — two finished remixes by one person
 *   luna → dan1 → eve1     two rows — Eve continuing Dan's does not erase his
 *
 * `takeCount` is how many passes that producer made in a row to reach this
 * tip; `depth` stays where the tip sits, which is what an indent means.
 */
function branchTips(items: RemixItem[], childrenOf: Map<string, TreeNode[]>): RemixItem[] {
  const byId = new Map(items.map((i) => [i.id, i]));
  /** The newest note in this producer's own run of takes, tip first. */
  const noteOf = (item: RemixItem, all: Map<string, RemixItem>): string | null => {
    let at: RemixItem | undefined = item;
    while (at) {
      if (at.note) return at.note;
      const parent: RemixItem | undefined = at.parentId ? all.get(at.parentId) : undefined;
      at = parent && parent.producer.id === item.producer.id ? parent : undefined;
    }
    return null;
  };
  const chain = (item: RemixItem): number => {
    let n = 1;
    let parent = byId.get(item.id)?.parentId ?? null;
    while (parent) {
      const up = byId.get(parent);
      if (!up || up.producer.id !== item.producer.id) break;
      n++;
      parent = up.parentId ?? null;
    }
    return n;
  };
  return items
    .filter((item) => !(childrenOf.get(item.id) ?? []).some((c) => c.producer.id === item.producer.id))
    .map((item) => {
      const takeCount = chain(item);
      return {
        ...item,
        takeCount,
        branchStart: item.depth - takeCount + 1,
        // The tip is often a silent re-save; the thing they SAID about this
        // remix is the newest note anywhere in their own chain.
        note: noteOf(item, byId),
      };
    });
}

/**
 * Everything downstream of the focus seed, split by who made it.
 *
 * The walk goes DOWN only — the tree holds this seed's ancestors too, and
 * counting those as remixes of it would credit the original to its own remix.
 * It descends THROUGH the focus producer's own takes rather than stopping at
 * them, so a stranger's remix of your v3 still appears (at depth 2+); those
 * takes come back separately as `versions`, because "1 remix" for your own
 * next version is exactly the confusion this is here to end.
 */
export function readRemixes(
  tree: Pick<SeedTree, "focusId" | "nodes" | "truncated"> | undefined,
  opts: { limit?: number } = {},
): RemixReading {
  if (!tree) return EMPTY;
  const focus = tree.nodes.find((n) => n.id === tree.focusId);
  if (!focus) return EMPTY;

  const children = new Map<string, TreeNode[]>();
  for (const n of tree.nodes) {
    if (n.parentSeedId === null) continue;
    children.set(n.parentSeedId, [...(children.get(n.parentSeedId) ?? []), n]);
  }

  const remixes: RemixItem[] = [];
  const versions: RemixItem[] = [];
  const seen = new Set<string>([focus.id]);
  let frontier: Array<{ id: string; depth: number }> = [{ id: focus.id, depth: 0 }];
  // Bounded by the node count: a parent cycle cannot hang this.
  while (frontier.length > 0) {
    const next: Array<{ id: string; depth: number }> = [];
    for (const { id, depth } of frontier) {
      for (const child of children.get(id) ?? []) {
        if (seen.has(child.id)) continue;
        seen.add(child.id);
        const item = itemOf(child, depth + 1);
        (child.producer.id === focus.producer.id ? versions : remixes).push(item);
        next.push({ id: child.id, depth: depth + 1 });
      }
    }
    frontier = next;
  }

  const newestFirst = (a: RemixItem, b: RemixItem): number =>
    b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id);
  const people = branchTips(remixes, children).sort(newestFirst);
  versions.sort(newestFirst);

  const limit = opts.limit ?? people.length;
  return {
    items: people.slice(0, limit),
    total: people.length,
    versions,
    truncated: tree.truncated || people.length > limit,
  };
}

/** "Remix of Untitled Seed by Lunvs" — the aria-label, the title=, the unfurl. */
export function remixCreditText(origin: RemixOrigin): string {
  return `Remix of ${origin.title} by ${origin.producer.username}`;
}
